/**
 * Hotel review scores, kept with the scale they were given on.
 *
 * The two hotel sources score on different scales: Duffel's `review_score`
 * runs to 10 and Google Hotels' `overall_rating` runs to 5. Every screen used
 * to print the score as "/5", so the Stays page showed "Opera House Hotel
 * 8.9/5" and five full stars for The Gallivant's 5.9 out of 10 (2026-10-08).
 *
 * Each source now tags its results with `rating_scale`. The select endpoints
 * store it as `stays.metadata.rating_scale`. Screens go through
 * `resolveHotelRating`, which prints the score out of its own scale and puts
 * the star row on five.
 *
 * Plain JS with no imports: server/duffel-stays.js, server/google-hotels.js
 * and server.js import the scale constants from here.
 */

/** Duffel Stays `accommodation.review_score`: 1–10. */
export const DUFFEL_REVIEW_SCALE = 10;
/** Google Hotels (SerpAPI) `overall_rating`: 1–5. */
export const GOOGLE_HOTELS_REVIEW_SCALE = 5;

const KNOWN_SCALES = new Set([DUFFEL_REVIEW_SCALE, GOOGLE_HOTELS_REVIEW_SCALE]);

const isDuffelId = (id) => typeof id === 'string' && id.startsWith('srr_');

/**
 * The scale a hotel's `rating` is on, or null when it can't be told.
 *
 * Takes any shape a hotel travels in: a search result, a `stays` row, a
 * booking's `stay_snapshot` or `flight_data.stay_data`, or a StayBooking
 * entity (camelCase). Rows saved before the scale was stored carry their
 * source's marker instead: on the live book (2026-10-08) every Duffel row has
 * an `srr_` search-result id and `metadata.accommodation_id`, and every Google
 * row has `metadata.property_token`.
 */
export function hotelRatingScale(hotel) {
  if (!hotel || typeof hotel !== 'object') return null;
  const meta = hotel.metadata && typeof hotel.metadata === 'object' ? hotel.metadata : {};

  for (const tagged of [hotel.rating_scale, hotel.ratingScale, meta.rating_scale]) {
    const scale = Number(tagged);
    if (KNOWN_SCALES.has(scale)) return scale;
  }

  if (
    meta.accommodation_id
    || [hotel.search_result_id, hotel.serpapi_hotel_id, hotel.serpapiHotelId, hotel.id].some(isDuffelId)
  ) {
    return DUFFEL_REVIEW_SCALE;
  }
  if (meta.property_token) return GOOGLE_HOTELS_REVIEW_SCALE;

  // A score above 5 can't be out of 5.
  if (Number(hotel.rating) > GOOGLE_HOTELS_REVIEW_SCALE) return DUFFEL_REVIEW_SCALE;
  return null;
}

/**
 * A hotel's review score ready to show, or null when there is no score or
 * its scale is unknown. A number is never printed without its scale.
 *
 *   score      the source's own number, e.g. 8.8
 *   scale      what it is out of, e.g. 10
 *   outOfFive  the same score on five stars, for star rows, e.g. 4.4
 *   label      "8.8/10"
 */
export function resolveHotelRating(hotel) {
  const score = Number(hotel?.rating);
  if (!Number.isFinite(score) || score <= 0) return null;
  const scale = hotelRatingScale(hotel);
  if (!scale || score > scale) return null;
  return {
    score,
    scale,
    outOfFive: (score / scale) * 5,
    label: `${score.toFixed(1)}/${scale}`,
  };
}

/**
 * Sort comparator: best-reviewed first, compared on one scale so a Duffel
 * 8.8/10 and a Google 4.6/5 rank correctly against each other. Unrated
 * hotels go last.
 */
export function compareHotelsByRating(a, b) {
  return (resolveHotelRating(b)?.outOfFive ?? 0) - (resolveHotelRating(a)?.outOfFive ?? 0);
}
